import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { trustedWorkflowReturnRoute } from '@app/shared/workflow-navigation/workflow-return-route';
import { EzButtonComponent } from '@template-ui/components/ez-button/ez-button.component';
import { WorkflowCompletionChecklistComponent } from '@app/workflow-workspace/workflow-completion-checklist.component';
import { HearingWorkflowTaskAuthorityService } from './hearing-workflow-task-authority.service';

@Component({
  selector: 'phms-hearing-workflow-task-panel',
  standalone: true,
  imports: [CommonModule, RouterLink, EzButtonComponent, WorkflowCompletionChecklistComponent],
  template: `
    <section class="workflow-task" aria-label="Current workflow task" data-testid="hearing-workflow-task">
      <header>
        <strong>Workflow task</strong>
        <span role="status" aria-live="polite">{{ progressLabel() }}</span>
        <ui-button size="small" severity="secondary" [disabled]="authority.loading()"
          (click)="refresh()" data-testid="refresh-hearing-workflow">Refresh task</ui-button>
      </header>
      <p *ngIf="authority.error()" role="alert">{{ authority.error() }}</p>
      <ng-container *ngIf="authority.snapshot() as task">
        <p *ngIf="task.link.availability === 'unavailable'" data-testid="hearing-workflow-unavailable">
          {{ unavailableMessage(task.link.reasonCode) }} Record editing remains available below.
        </p>
        <ng-container *ngIf="task.availableActions as current">
          <p *ngFor="let reason of current.blockReasons">{{ reason.message }}</p>
          <ul *ngIf="current.actions.length" class="task-actions" aria-label="Server supplied actions">
            <li *ngFor="let action of current.actions" [attr.data-enabled]="action.enabled">
              <strong>{{ action.label }}</strong> · {{ action.enabled ? 'Available' : 'Unavailable' }}
              <span *ngFor="let reason of action.blockReasons"> · {{ reason.message }}</span>
            </li>
          </ul>
          <p *ngIf="!current.actions.length && !current.blockReasons.length">No action is available for your current access.</p>
          <a [routerLink]="task.link.route" [queryParams]="taskQueryParams()"
            data-testid="open-hearing-workflow-task">Open workflow task</a>
          <details *ngIf="task.requirements && task.requirements.requirements.length">
            <summary>Requirement evidence for this visit</summary>
            <phms-workflow-completion-checklist [checklist]="task.requirements" [readOnly]="true" />
          </details>
        </ng-container>
      </ng-container>
    </section>
  `,
  styles: [`
    .workflow-task { padding: .65rem .85rem; margin-bottom: .75rem; border: 1px solid var(--surface-border); border-radius: var(--border-radius, 6px); background: var(--surface-card); }
    header { display: flex; align-items: center; flex-wrap: wrap; gap: .5rem .8rem; }
    header ui-button { margin-left: auto; }
    p, .task-actions { margin: .5rem 0; }
    .task-actions { padding-left: 1.2rem; }
    details { margin-top: .65rem; } summary { cursor: pointer; }
  `],
})
export class HearingWorkflowTaskPanelComponent implements OnChanges {
  @Input({ required: true }) scheduledProceedingId: number | null = null;
  readonly authority = inject(HearingWorkflowTaskAuthorityService);
  private readonly route = inject(ActivatedRoute);
  taskQueryParams(): Record<string, string> {
    const returnTo = trustedWorkflowReturnRoute(this.route.snapshot.queryParamMap?.get('returnTo'));
    return returnTo ? { returnTo } : {};
  }
  ngOnChanges(): void { this.refresh(); }
  refresh(): void { void this.authority.refresh(this.scheduledProceedingId ?? 0); }
  progressLabel(): string {
    if (this.authority.loading()) return 'Checking current requirements and actions…';
    const requirements = this.authority.snapshot()?.requirements;
    if (!requirements) return 'Workflow progress unavailable';
    if (!requirements.requirements.length) return 'No completion requirements configured for this visit';
    const supplied = requirements.requirements.filter(row => row.state === 'satisfied').length;
    return `${supplied} of ${requirements.requirements.length} requirements satisfied`;
  }
  unavailableMessage(reason: string): string {
    switch (reason) {
      case 'hearing_link_missing':
      case 'workflow_link_missing': return 'No workflow is linked to this hearing.';
      case 'workflow_adapter_unavailable': return 'This retained workflow has no installed task adapter.';
      case 'workflow_not_active': return 'This hearing has no active workflow visit.';
      case 'current_occurrence_missing':
      case 'current_occurrence_ambiguous': return 'The current workflow visit could not be confirmed.';
      default: return 'A supported workflow task is not available for this hearing. An administrator can check the explicit workflow link and retained definition.';
    }
  }
}
